import {
  AloftArea,
  DEFAULT_ECONOMIC_LIFE,
  DEFAULT_ECONOMIC_LIFE_MANUFACTURED,
  DefaultEcoLifeField,
  EcoLife,
  SubjectiveCharacteristic,
  getFoundationDisplay,
  isDefined,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { DISCLOSABLE_FEATURES, FEATURES, Feature } from '../../../constants.ts';
import { selectDepreciationSettings } from '../orderSelectors.ts';
import { selectEditablePropertyDataEntities } from './editablePropertyDataSelectors.ts';

export const selectImprovementsEntities = createSelector(
  [selectEditablePropertyDataEntities],
  (editablePropertyDataEntities) => {
    const entities = Object.fromEntries(
      Object.entries(editablePropertyDataEntities).map(([id, editableData]) => [
        id,
        { id, ...editableData.improvements },
      ]),
    );

    // Add a separate entry for subjectArv if it exists in editablePropertyDataEntities
    if (
      editablePropertyDataEntities.subject &&
      'arv' in editablePropertyDataEntities.subject &&
      editablePropertyDataEntities.subject.arv
    ) {
      entities.subjectArv = {
        id: 'subjectArv',
        ...editablePropertyDataEntities.subject.arv.improvements,
      };
    }

    return entities;
  },
);

export const selectImprovementsById = createSelector(
  [selectImprovementsEntities, (_state, id: string) => id],
  (improvementsEntities, id) => improvementsEntities[id],
);

export const selectImprovementEntityIds = createSelector(
  [selectImprovementsEntities],
  (improvementsEntities) => Object.keys(improvementsEntities),
);

export const selectAllImprovements = createSelector(
  [selectImprovementsEntities],
  (improvementsEntities) => Object.values(improvementsEntities).sort((a, b) => {
    if (a.id === 'subject' || a.id === 'subjectArv') return -1;
    if (b.id === 'subject' || b.id === 'subjectArv') return 1;
    return a.id.localeCompare(b.id);
  }),
);

export const selectSubjectiveCharacteristicsByIdAndField = createSelector(
  [
    selectImprovementsEntities,
    (state, id: string) => id,
    (state, id: string, field: SubjectiveCharacteristic) => field,
  ],
  (improvementsEntities, id, field) => improvementsEntities?.[id]?.[field],
);

export const selectEcoLifeByIdAndField = createSelector(
  [
    selectImprovementsEntities,
    selectDepreciationSettings,
    (state, id: string) => id,
    (state, id: string, field: EcoLife) => field,
    (state, id: string, field: EcoLife, defaultEcoLifeField: DefaultEcoLifeField) =>
      defaultEcoLifeField,
  ],
  (improvementsEntities, depreciationSettings, id, field, defaultEcoLifeField) => {
    // use manufactured economic life for the main dwelling of a manufactured home
    const defaultEconomicLife =
      field === 'totalEconomicLife' &&
      improvementsEntities?.[id]?.propertyType === 'manufacturedHome'
        ? (depreciationSettings[defaultEcoLifeField] ??
          depreciationSettings.defaultEcoLifeManufactured ??
          DEFAULT_ECONOMIC_LIFE_MANUFACTURED)
        : (depreciationSettings[defaultEcoLifeField] ??
          depreciationSettings.defaultEcoLife ??
          DEFAULT_ECONOMIC_LIFE);

    return improvementsEntities?.[id]?.[field] ?? defaultEconomicLife;
  },
);

export const selectImprovementsDisplay = createSelector(
  [selectImprovementsById],
  (improvementsForTransaction) => {
    if (isUndefined(improvementsForTransaction)) return {};

    const {
      gla,
      fullBaths = 0,
      halfBaths = 0,
      uadQuality: quality,
      uadCondition: condition,
      age,
      craftsmanQuality,
      effectiveAge,
    } = improvementsForTransaction;

    const foundation =
      improvementsForTransaction.propertyType === 'manufacturedHome'
        ? improvementsForTransaction.foundation
        : undefined;
    const numberOfUnits =
      improvementsForTransaction.propertyType === 'multiFamily'
        ? improvementsForTransaction.numberOfUnits
        : undefined;

    const foundationDisplayLabel = getFoundationDisplay(foundation) ?? '-';

    return {
      foundation: foundationDisplayLabel,
      gla: isDefined(gla)
        ? AloftArea.schema.parse(gla).toString().replace('sqft', 'sf')
        : undefined,
      numberOfUnits: isDefined(numberOfUnits) ? `Units ${numberOfUnits}` : undefined,
      baths: `Baths ${fullBaths}.${halfBaths}`,
      quality: isDefined(quality) ? `Q${quality}` : undefined,
      condition: isDefined(condition) ? `C${condition}` : undefined,
      age: age?.toString(),
      craftsmanQuality,
      effectiveAge: `${effectiveAge}`,
    };
  },
);

export const selectQualityDisplay = createSelector(
  [selectImprovementsById],
  (improvementsForTransaction) => {
    if (isUndefined(improvementsForTransaction)) return undefined;

    const { uadQuality: quality, craftsmanQuality } = improvementsForTransaction;

    return `Qlty ${isDefined(quality) ? `Q${quality}` : '-'}/${craftsmanQuality ?? '-'}`;
  },
);

export const selectAdditionalStructureTotalArea = createSelector(
  [selectImprovementsById],
  (improvementsForTransaction) => {
    const totalAreaOfAdditionalStructures = [
      improvementsForTransaction?.adu?.area,
      improvementsForTransaction?.garage?.area,
      improvementsForTransaction?.attic?.area,
      improvementsForTransaction?.shed?.area,
      improvementsForTransaction?.spa?.area,
      improvementsForTransaction?.pool?.area,
      improvementsForTransaction?.workshop?.area,
    ]
      .map((area) => (area ? AloftArea.schema.parse(area) : AloftArea.zero))
      .reduce((acc, area) => acc.add(area), AloftArea.zero);

    return totalAreaOfAdditionalStructures;
  },
);

export const selectFormDisclosureState = createSelector(
  [selectImprovementsEntities, (_state, id: string) => id],
  (improvements, id) => {
    const improvementsForTransaction = improvements[id];

    const disclosureState: Feature[] = [];

    if (isUndefined(improvementsForTransaction)) return disclosureState;

    DISCLOSABLE_FEATURES.forEach((feature) => {
      if (FEATURES[feature].hasFeature(improvementsForTransaction)) {
        disclosureState.push(feature);
      }
    });

    return disclosureState;
  },
);

export const selectFeatureDetailsByIDAndFeature = createSelector(
  [selectImprovementsById, (state, id: string, feature: Feature) => feature],
  (improvementsForTransaction, feature) => {
    const { label, getDetails, hasFeature } = FEATURES[feature];

    if (
      isUndefined(improvementsForTransaction) ||
      isUndefined(label) ||
      isUndefined(getDetails) ||
      isUndefined(hasFeature) ||
      !hasFeature(improvementsForTransaction)
    ) {
      return '-';
    }

    if (feature === 'manualCost' && improvementsForTransaction.manualCostDisplayName) {
      return improvementsForTransaction.manualCostDisplayName;
    }

    return `${label} ${getDetails(improvementsForTransaction)}`;
  },
);

export const selectImprovementsGla = createSelector(
  [selectImprovementsById],
  (improvementsForTransaction) => {
    if (isUndefined(improvementsForTransaction)) return undefined;

    return isDefined(improvementsForTransaction.gla)
      ? AloftArea.schema.parse(improvementsForTransaction.gla)
      : undefined;
  },
);

export const selectImprovementsBasement = createSelector(
  [selectImprovementsById],
  (improvementsForTransaction) => {
    if (isUndefined(improvementsForTransaction)) return undefined;

    return isDefined(improvementsForTransaction.basement?.totalArea)
      ? AloftArea.schema.parse(improvementsForTransaction.basement?.totalArea)
      : undefined;
  },
);

export const selectImprovementsGarageCarport = createSelector(
  [selectImprovementsById],
  (improvementsForTransaction) => {
    const { carport, detachedGarage, garage } = improvementsForTransaction ?? {};
    if (!carport && !detachedGarage && !garage) return undefined;

    const carportArea = isDefined(carport?.area)
      ? AloftArea.schema.parse(carport?.area)
      : AloftArea.fromNumber(0, 'sqft');
    const detachedGarageArea = isDefined(detachedGarage?.area)
      ? AloftArea.schema.parse(detachedGarage?.area)
      : AloftArea.fromNumber(0, 'sqft');
    const garageArea = isDefined(garage?.area)
      ? AloftArea.schema.parse(garage?.area)
      : AloftArea.fromNumber(0, 'sqft');

    return garageArea.add(carportArea).add(detachedGarageArea);
  },
);

export const selectSubjectIsManufacturedHome = createSelector(
  [(state) => selectImprovementsById(state, 'subject')],
  (subjectImprovements) => {
    if (isUndefined(subjectImprovements)) return false;
    return subjectImprovements.propertyType === 'manufacturedHome';
  },
);
